import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { addDays, endOfDay, isBefore } from "date-fns";
import { asUTC } from "utils/timezone";
import {
  dataSourceIdToDSName,
  genericErrorMessage,
  MSP_FEATURE,
  PRODUCT_FEATURE,
  PRODUCT_ID,
  storageProviderNameToId,
} from "consts";
import { showAlert } from "slices/alertsSlice";
import ModalForm from "shared/ModalForm";
import Loader from "@druva-apps/orion/lib/loader";

import FormContainer from "./FormContainer";
import useUpdateMSPForm from "./useUpdateMSPForm";

const validationSchemaCreator = (Yup) => {
  return Yup.object().shape({
    organizationName: Yup.string().max(30, "Max 30 chars").required("Required"),
    sfdcId: Yup.string()
      .required("Required")
      .matches(/^[a-z0-9]+$/i, {
        excludeEmptyString: true,
        message: "Invalid SFDC ID. Only alphanumeric characters allowed.",
      })
      .test(
        "start",
        "Must start with '001'",
        (val) => (val || "").substr(0, 3) === "001"
      )
      .test(
        "length",
        "Must be exactly 15 or 18 characters",
        (val) => (val || "").length === 15 || (val || "").length === 18
      ),
    edition: Yup.mixed().required("Required"),
    parentMsp: Yup.mixed().nullable(),
    expiryDate: Yup.date()
      .nullable()
      .required("Required")
      .test(
        "edition",
        "You can evaluate for a maximum of 30 days",
        function (value) {
          const { edition } = this.parent;
          if (edition?.value === "evaluation") {
            return isBefore(value, addDays(endOfDay(new Date()), 31));
          }
          return true;
        }
      ),
    products: Yup.array()
      .of(
        Yup.object().shape({
          skus: Yup.array().test("enabled", "Required", function (value) {
            const { enabled } = this.parent;
            if (enabled) {
              return value.length > 0;
            }
            return true;
          }),
          awsStorageRegions: Yup.array().test(
            "enabled",
            "Required",
            function (value) {
              const { enabled } = this.parent;
              if (enabled) {
                return value.length > 0;
              }
              return true;
            }
          ),
          azureStorageRegions: Yup.array(),
          datasources: Yup.array().test(
            "enabled",
            "Required",
            function (value) {
              const { enabled, productID } = this.parent;
              if (enabled && productID === PRODUCT_ID.saasAppsAndEndpoints) {
                return value.length > 0;
              }
              return true;
            }
          ),
        })
      )
      .test("products", function (value) {
        return value.some((v) => v.enabled);
      }),
  });
};

const MSPForm = ({ closeModal, header, isOpen, mspId }) => {
  const dispatch = useDispatch();

  const [
    {
      initialValues,
      isLoadingMspList,
      parentMspOptions,
      isParentMsp,
      isMspListLoading,
    },
    { mutate },
  ] = useUpdateMSPForm(mspId);

  const onSubmitHandler = useCallback(
    (
      {
        expiryDate,
        organizationName,
        edition,
        sfdcId,
        products,
        parentMsp,
        druInvestigate,
      },
      formik
    ) => {
      mutate(
        {
          organizationName,
          sfdcId,
          edition: edition?.value,
          expiryDate: parentMsp?.value
            ? parentMspOptions.find((msp) => msp.value === parentMsp.value)
                ?.expiryDate ?? asUTC(endOfDay(expiryDate)).toISOString()
            : asUTC(endOfDay(expiryDate)).toISOString(),
          parentId: parentMsp?.value || null,
          features: [
            {
              name: MSP_FEATURE.druInvestigate,
              isEnabled: !!druInvestigate,
            },
          ],
          products: products
            ?.filter(({ enabled }) => enabled)
            .map(
              ({
                productID,
                datasources,
                skus,
                awsStorageRegions,
                azureStorageRegions,
                educationLicense,
              }) => {
                let selectedDatasource =
                  productID === PRODUCT_ID.enterpriseWorkloads
                    ? [
                        {
                          name: "Enterprise Workloads",
                          isEnabled: true,
                        },
                      ]
                    : datasources.map((datasource) => ({
                        name: dataSourceIdToDSName[datasource.value],
                        isEnabled: true,
                      }));

                if (productID === PRODUCT_ID.saasAppsAndEndpoints) {
                  const initialDataSource = initialValues.products?.find(
                    (product) =>
                      product.productID === PRODUCT_ID.saasAppsAndEndpoints
                  )?.datasources;

                  selectedDatasource = [
                    ...selectedDatasource,
                    ...(initialDataSource || [])
                      .filter(
                        (datasource) =>
                          !selectedDatasource.some(
                            (ds) =>
                              ds.name === dataSourceIdToDSName[datasource.value]
                          )
                      )
                      ?.map((ds) => ({
                        name: dataSourceIdToDSName[ds.value],
                        isEnabled: false,
                      })),
                  ];
                }

                const productsPayload = {
                  productID,
                  skus: skus.map(({ value }) => value),
                  datasources: selectedDatasource,
                };

                const awsStorageRegionsPayload = awsStorageRegions.map(
                  (awsStorageRegion) => ({
                    name: awsStorageRegion.value,
                    storageProvider: storageProviderNameToId.aws,
                  })
                );

                if (productID === PRODUCT_ID.enterpriseWorkloads) {
                  productsPayload.storageRegions = [
                    ...awsStorageRegionsPayload,
                    ...azureStorageRegions.map((azureStorageRegion) => ({
                      name: azureStorageRegion.value,
                      storageProvider: storageProviderNameToId.azure,
                    })),
                  ];
                }

                if (productID === PRODUCT_ID.saasAppsAndEndpoints) {
                  productsPayload.storageRegions = [
                    ...awsStorageRegionsPayload,
                    ...azureStorageRegions.map((azureStorageRegion) => ({
                      name: azureStorageRegion.value,
                      storageProvider: storageProviderNameToId.azure,
                    })),
                  ];
                  productsPayload.features = [
                    {
                      name: PRODUCT_FEATURE.educationLicense,
                      isEnabled: educationLicense,
                    },
                  ];
                }

                return productsPayload;
              }
            ),
        },
        {
          onError: (e) => {
            if (e.status === 411) {
              // TODO update after initialErrors props will be handled by Form
              formik.setErrors(e.data);
            } else {
              dispatch(
                showAlert({
                  message: e?.message || genericErrorMessage,
                  type: "danger",
                })
              );
            }
          },
          onSuccess: (data) => {
            if (data?.warning) {
              dispatch(
                showAlert({
                  message: data?.message,
                  type: "warning",
                })
              );
            } else {
              dispatch(
                showAlert({
                  message: "MSP account edited successfully",
                  type: "success",
                })
              );
            }
            closeModal();
          },
        }
      );
    },
    [mutate, dispatch, closeModal, initialValues, parentMspOptions]
  );

  if (!initialValues) {
    return <Loader isLoading fullPage />;
  }

  return (
    <ModalForm
      disableSubmitWhenInvalid
      closeModal={closeModal}
      withValidation
      header={header}
      isOpen={isOpen}
      size="lg"
      onSubmit={onSubmitHandler}
      initialValues={initialValues}
      validationSchema={validationSchemaCreator}
      renderChildren={(props) => (
        <FormContainer
          formikProps={props}
          isEdit
          parentMspOptions={parentMspOptions}
          isLoadingMspList={isLoadingMspList}
          isParentMsp={isParentMsp}
          isMspListLoading={isMspListLoading}
        />
      )}
      submitButtonLabel="Save"
    />
  );
};

export default MSPForm;
